import React, { createContext, useContext, useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { Spin } from 'antd';
import { useAuth } from '@/hooks/useAuth';
import { useClientsWithCompaniesQuery } from '@/api/auth/queries';
import type { ClientWithCompanies, Company } from '@/api/auth/interfaces';
import { canUseAdminTenantScope } from '@/utils/authz';
import { applyAdminScopeToUrl, readAdminScopeFromUrl } from '@/utils/adminScopeUrl';

interface AdminClientsContextValue {
  /** All clients (for admin users) */
  clients: ClientWithCompanies[];
  /** Loading state */
  loading: boolean;
  /** Check if clients are available */
  hasClients: boolean;
  /** Currently selected client ID (from URL) */
  selectedClientId: string | null;
  /** Set selected client ID (updates URL) */
  setSelectedClientId: (clientId: string | null) => void;
  /** Currently selected company ID (from URL); only valid when selected client has companies */
  selectedCompanyId: string | null;
  /** Set selected company ID (updates URL) */
  setSelectedCompanyId: (companyId: string | null) => void;
  /** Currently selected customer user ID (from URL); used on /client for admin impersonation */
  selectedCustomerId: string | null;
  /** Set selected customer user ID (updates URL) */
  setSelectedCustomerId: (customerId: string | null) => void;
  /** Companies for the currently selected client; empty if client has none */
  companiesForSelectedClient: Company[];
  /** Is viewing all clients (no selection) */
  isViewingAllClients: boolean;
  /** Is viewing specific client */
  isViewingSpecificClient: boolean;
  /** Is in manage or client namespace */
  isInManageOrClientNamespace: boolean;
  /** Is in customer portal namespace */
  isInClientNamespace: boolean;
  /** Selection is ready (always true since clients load before router) */
  isSelectionReady: boolean;
}

const AdminClientsContext = createContext<AdminClientsContextValue | undefined>(undefined);

interface AdminClientsProviderProps {
  children: React.ReactNode;
}

/**
 * Admin clients context provider for ADMIN/DEVELOPER impersonation scope.
 *
 * Selected client/company are stored in URL search params (`adminClientId`,
 * `adminCompanyId`) — the same names validated by TanStack Router in `__root.tsx`.
 *
 * Because this provider sits **above** RouterProvider, it reads/writes the URL
 * via the History API and intercepts `pushState`/`replaceState`. In-app selectors
 * should use {@link setSelectedClientId} / {@link setSelectedCompanyId} or TanStack
 * `navigate()` with the same param names (see `utils/adminScopeUrl.ts`).
 *
 * Regular users (USER/CUSTOMER) ignore this context; their tenant comes from JWT.
 */
export const AdminClientsProvider: React.FC<AdminClientsProviderProps> = ({ children }) => {
  const { payload } = useAuth();
  const isAdmin = canUseAdminTenantScope(payload?.role);
  
  // Fetch clients for admin users
  // Use a high limit to get all clients for the selector
  const { data: clientsData, isLoading } = useClientsWithCompaniesQuery({ page: 1, limit: 1000 }, isAdmin);
  
  const clients = useMemo(() => clientsData?.clients ?? [], [clientsData]);
  
  // Read pathname and search params from URL directly (since we're above RouterProvider)
  const [pathname, setPathname] = useState<string>(() => {
    return typeof window !== 'undefined' ? window.location.pathname : '';
  });
  
  const [urlAdminClientId, setUrlAdminClientId] = useState<string | null>(() =>
    readAdminScopeFromUrl().adminClientId
  );

  const [urlAdminCompanyId, setUrlAdminCompanyId] = useState<string | null>(() =>
    readAdminScopeFromUrl().adminCompanyId
  );

  const [urlAdminCustomerId, setUrlAdminCustomerId] = useState<string | null>(() =>
    readAdminScopeFromUrl().adminCustomerId
  );
  
  // Track last values to avoid unnecessary updates
  const lastPathnameRef = useRef(pathname);
  const lastAdminClientIdRef = useRef(urlAdminClientId);
  const lastAdminCompanyIdRef = useRef(urlAdminCompanyId);
  const lastAdminCustomerIdRef = useRef(urlAdminCustomerId);
  // Track the client/company/customer IDs we want to preserve across navigations
  const preserveClientIdRef = useRef<string | null>(urlAdminClientId);
  const preserveCompanyIdRef = useRef<string | null>(urlAdminCompanyId);
  const preserveCustomerIdRef = useRef<string | null>(urlAdminCustomerId);
  
  // Listen for URL changes (History API interception and popstate)
  useEffect(() => {
    const updateFromUrl = () => {
      if (typeof window === 'undefined') return;
      
      const currentPathname = window.location.pathname;
      const { adminClientId: currentAdminClientId, adminCompanyId: currentAdminCompanyId, adminCustomerId: currentAdminCustomerId } =
        readAdminScopeFromUrl();
      const urlParams = new URLSearchParams(window.location.search);
      
      // Check if pathname changed (navigation occurred)
      const pathnameChanged = lastPathnameRef.current !== currentPathname;
      
      // Check if we're in manage/client namespace and params are missing
      const isInNamespace = currentPathname.startsWith('/manage') || currentPathname.startsWith('/client');
      const isInClientNs = currentPathname.startsWith('/client');
      const shouldRestoreAdminClientId = 
        isAdmin && 
        preserveClientIdRef.current && 
        isInNamespace && 
        !currentAdminClientId;
      // Only restore company when client is unchanged - otherwise user intentionally switched client
      const shouldRestoreAdminCompanyId = 
        isAdmin && 
        preserveCompanyIdRef.current && 
        isInNamespace && 
        !currentAdminCompanyId &&
        currentAdminClientId === preserveClientIdRef.current;
      const shouldRestoreAdminCustomerId =
        isAdmin &&
        preserveCustomerIdRef.current &&
        isInClientNs &&
        !currentAdminCustomerId &&
        currentAdminClientId === preserveClientIdRef.current;
      
      // Only update if values actually changed
      if (pathnameChanged) {
        lastPathnameRef.current = currentPathname;
        setPathname(currentPathname);
      }
      
      // Restore missing admin scope params
      if (shouldRestoreAdminClientId || shouldRestoreAdminCompanyId || shouldRestoreAdminCustomerId) {
        if (preserveClientIdRef.current) urlParams.set('adminClientId', preserveClientIdRef.current);
        if (preserveCompanyIdRef.current) urlParams.set('adminCompanyId', preserveCompanyIdRef.current);
        if (preserveCustomerIdRef.current && isInClientNs) {
          urlParams.set('adminCustomerId', preserveCustomerIdRef.current);
        }
        const newUrl = `${currentPathname}?${urlParams.toString()}`;
        window.history.replaceState({}, '', newUrl);
        if (shouldRestoreAdminClientId && lastAdminClientIdRef.current !== preserveClientIdRef.current) {
          lastAdminClientIdRef.current = preserveClientIdRef.current;
          setUrlAdminClientId(preserveClientIdRef.current);
        }
        if (shouldRestoreAdminCompanyId && lastAdminCompanyIdRef.current !== preserveCompanyIdRef.current) {
          lastAdminCompanyIdRef.current = preserveCompanyIdRef.current;
          setUrlAdminCompanyId(preserveCompanyIdRef.current);
        }
        if (shouldRestoreAdminCustomerId && lastAdminCustomerIdRef.current !== preserveCustomerIdRef.current) {
          lastAdminCustomerIdRef.current = preserveCustomerIdRef.current;
          setUrlAdminCustomerId(preserveCustomerIdRef.current);
        }
        return;
      }
      
      if (lastAdminClientIdRef.current !== currentAdminClientId) {
        lastAdminClientIdRef.current = currentAdminClientId;
        setUrlAdminClientId(currentAdminClientId);
        preserveClientIdRef.current = currentAdminClientId;
        preserveCompanyIdRef.current = null; // Clear when client changes
        preserveCustomerIdRef.current = null;
      }
      if (lastAdminCompanyIdRef.current !== currentAdminCompanyId) {
        lastAdminCompanyIdRef.current = currentAdminCompanyId;
        setUrlAdminCompanyId(currentAdminCompanyId);
        preserveCompanyIdRef.current = currentAdminCompanyId;
      }
      if (lastAdminCustomerIdRef.current !== currentAdminCustomerId) {
        lastAdminCustomerIdRef.current = currentAdminCustomerId;
        setUrlAdminCustomerId(currentAdminCustomerId);
        preserveCustomerIdRef.current = currentAdminCustomerId;
      }
    };
    
    // Intercept History API methods
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const debouncedUpdate = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(updateFromUrl, 10);
    };
    
    history.pushState = function(...args) {
      originalPushState.apply(history, args);
      debouncedUpdate();
    };
    
    history.replaceState = function(...args) {
      originalReplaceState.apply(history, args);
      debouncedUpdate();
    };
    
    // Listen for browser navigation (back/forward)
    window.addEventListener('popstate', updateFromUrl);
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', updateFromUrl);
    };
  }, [isAdmin]);
  
  // Check if we're in manage or client namespace
  const isInManageOrClientNamespace = useMemo(() => {
    return pathname.startsWith('/manage') || pathname.startsWith('/client');
  }, [pathname]);

  const isInClientNamespace = useMemo(() => pathname.startsWith('/client'), [pathname]);
  
  // Get selected client ID from URL
  const selectedClientId = useMemo(() => {
    if (!isAdmin) return null;
    return urlAdminClientId;
  }, [isAdmin, urlAdminClientId]);

  // Companies for the selected client (empty if client has none)
  const companiesForSelectedClient = useMemo(() => {
    if (!selectedClientId) return [];
    const client = clients.find((c) => c.id === selectedClientId);
    return client?.companies ?? [];
  }, [clients, selectedClientId]);

  // Get selected company ID from URL; valid only when client has companies
  const selectedCompanyId = useMemo(() => {
    if (!isAdmin) return null;
    const raw = urlAdminCompanyId;
    if (!raw || companiesForSelectedClient.length === 0) return null;
    const valid = companiesForSelectedClient.some((c) => c.id === raw);
    return valid ? raw : null;
  }, [isAdmin, urlAdminCompanyId, companiesForSelectedClient]);

  const selectedCustomerId = useMemo(() => {
    if (!isAdmin || !isInClientNamespace) return null;
    return urlAdminCustomerId;
  }, [isAdmin, isInClientNamespace, urlAdminCustomerId]);
  
  // Set selected client ID (updates URL via History API; clears company/customer when client changes)
  const setSelectedClientId = useCallback((clientId: string | null) => {
    if (!isAdmin) return;

    if ((clientId ?? null) === selectedClientId) {
      return;
    }

    preserveClientIdRef.current = clientId;
    preserveCompanyIdRef.current = null;
    preserveCustomerIdRef.current = null;

    if (typeof window !== 'undefined') {
      const newHref = applyAdminScopeToUrl(window.location.href, {
        adminClientId: clientId,
        adminCompanyId: null,
        adminCustomerId: null
      });
      window.history.replaceState({}, '', newHref);

      const { adminClientId: newAdminClientId } = readAdminScopeFromUrl(newHref);
      if (lastAdminClientIdRef.current !== newAdminClientId) {
        lastAdminClientIdRef.current = newAdminClientId;
        setUrlAdminClientId(newAdminClientId);
      }
      lastAdminCompanyIdRef.current = null;
      setUrlAdminCompanyId(null);
      lastAdminCustomerIdRef.current = null;
      setUrlAdminCustomerId(null);
    }
  }, [isAdmin, selectedClientId]);

  // Set selected company ID (updates URL via History API)
  const setSelectedCompanyId = useCallback((companyId: string | null) => {
    if (!isAdmin) return;
    if ((companyId ?? null) === selectedCompanyId) return;
    preserveCompanyIdRef.current = companyId;
    if (typeof window !== 'undefined') {
      const newHref = applyAdminScopeToUrl(window.location.href, { adminCompanyId: companyId });
      window.history.replaceState({}, '', newHref);
      const { adminCompanyId: newAdminCompanyId } = readAdminScopeFromUrl(newHref);
      if (lastAdminCompanyIdRef.current !== newAdminCompanyId) {
        lastAdminCompanyIdRef.current = newAdminCompanyId;
        setUrlAdminCompanyId(newAdminCompanyId);
      }
    }
  }, [isAdmin, selectedCompanyId]);

  const setSelectedCustomerId = useCallback((customerId: string | null) => {
    if (!isAdmin) return;
    if ((customerId ?? null) === selectedCustomerId) return;
    preserveCustomerIdRef.current = customerId;
    if (typeof window !== 'undefined') {
      const newHref = applyAdminScopeToUrl(window.location.href, { adminCustomerId: customerId });
      window.history.replaceState({}, '', newHref);
      const { adminCustomerId: newAdminCustomerId } = readAdminScopeFromUrl(newHref);
      if (lastAdminCustomerIdRef.current !== newAdminCustomerId) {
        lastAdminCustomerIdRef.current = newAdminCustomerId;
        setUrlAdminCustomerId(newAdminCustomerId);
      }
    }
  }, [isAdmin, selectedCustomerId]);

  // When selected client changes and current company is invalid, clear adminCompanyId from URL
  useEffect(() => {
    if (!isAdmin || !urlAdminCompanyId || isLoading) return;
    const valid = companiesForSelectedClient.some((c) => c.id === urlAdminCompanyId);
    if (!valid && typeof window !== 'undefined') {
      preserveCompanyIdRef.current = null;
      lastAdminCompanyIdRef.current = null;
      setUrlAdminCompanyId(null);
      const newHref = applyAdminScopeToUrl(window.location.href, { adminCompanyId: null });
      window.history.replaceState({}, '', newHref);
    }
  }, [isAdmin, selectedClientId, urlAdminCompanyId, companiesForSelectedClient, isLoading]);
  
  // Auto-select first client if no client is selected and clients are available
  // Only in manage/client namespace
  const hasAutoSelectedRef = useRef(false);
  useEffect(() => {
    if (
      isAdmin &&
      isInManageOrClientNamespace &&
      clients.length > 0 &&
      !selectedClientId &&
      !hasAutoSelectedRef.current
    ) {
      hasAutoSelectedRef.current = true;
      setSelectedClientId(clients[0].id);
    }
  }, [isAdmin, isInManageOrClientNamespace, clients, selectedClientId, setSelectedClientId]);
  
  // Reset auto-select flag when navigating away from manage/client namespace
  useEffect(() => {
    if (!isInManageOrClientNamespace) {
      hasAutoSelectedRef.current = false;
    }
  }, [isInManageOrClientNamespace]);

  const contextValue = useMemo<AdminClientsContextValue>(() => {
    return {
      clients,
      loading: isLoading,
      hasClients: clients.length > 0,
      selectedClientId,
      setSelectedClientId,
      selectedCompanyId,
      setSelectedCompanyId,
      selectedCustomerId,
      setSelectedCustomerId,
      companiesForSelectedClient,
      isViewingAllClients: selectedClientId === null,
      isViewingSpecificClient: selectedClientId !== null,
      isInManageOrClientNamespace,
      isInClientNamespace,
      isSelectionReady: !isLoading
    };
  }, [
    clients,
    isLoading,
    selectedClientId,
    setSelectedClientId,
    selectedCompanyId,
    setSelectedCompanyId,
    selectedCustomerId,
    setSelectedCustomerId,
    companiesForSelectedClient,
    isInManageOrClientNamespace,
    isInClientNamespace
  ]);

  // For admin users, don't render children until clients are loaded
  // This ensures the client selector has data before routes render
  if (isAdmin && isLoading) {
    return (
      <div
        style={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px'
        }}>
        <Spin size="large" />
        <div style={{ color: '#666', fontSize: '14px' }}>
          Loading clients...
        </div>
      </div>
    );
  }

  return (
    <AdminClientsContext.Provider value={contextValue}>
      {children}
    </AdminClientsContext.Provider>
  );
};

/**
 * Hook to access admin clients context
 */
export function useAdminClients() {
  const context = useContext(AdminClientsContext);
  if (!context) {
    throw new Error('useAdminClients must be used within AdminClientsProvider');
  }
  return context;
}

/**
 * Hook to get the effective clientId for queries
 * Returns the admin's selected clientId if admin, otherwise the user's clientId from payload
 * This matches the logic used in ModuleContext
 * 
 * Note: This hook can be used even if AdminClientsProvider is not available (for non-admin users)
 */
export function useEffectiveClientId(): string | null {
  const { payload } = useAuth();
  
  // Try to get admin selected client ID
  let adminSelectedClientId: string | null = null;
  try {
    const adminClients = useAdminClients();
    adminSelectedClientId = adminClients.selectedClientId;
  } catch {
    // AdminClientsProvider not available (non-admin users), fallback to payload
  }
  
  // Determine the client ID to use:
  // 1. If admin has selected a client, use that
  // 2. Otherwise, use the user's clientId from payload
  return adminSelectedClientId || payload?.clientId || null;
}

/**
 * Hook to get the effective companyId for queries.
 * For admin: returns selectedCompanyId (when in manage/client namespace); null when client has no companies.
 * For non-admin: returns payload.companyId from JWT.
 * When null, omit companyId from API params; do not send it.
 */
export function useEffectiveCompanyId(): string | null {
  const { payload } = useAuth();
  const isAdmin = canUseAdminTenantScope(payload?.role);
  let adminSelectedCompanyId: string | null = null;
  let isInNamespace = false;
  try {
    const adminClients = useAdminClients();
    adminSelectedCompanyId = adminClients.selectedCompanyId;
    isInNamespace = adminClients.isInManageOrClientNamespace;
  } catch {
    // AdminClientsProvider not available
  }
  if (isAdmin && isInNamespace) return adminSelectedCompanyId;
  return payload?.companyId ?? null;
}

/**
 * Hook to get the effective customer userId for /client portal queries.
 * For admin on /client: returns selectedCustomerId from the picker.
 * For real CUSTOMER users: returns payload.userId.
 */
export function useEffectiveCustomerId(): string | null {
  const { payload } = useAuth();
  const isAdmin = canUseAdminTenantScope(payload?.role);
  let adminSelectedCustomerId: string | null = null;
  let isInClientNs = false;
  try {
    const adminClients = useAdminClients();
    adminSelectedCustomerId = adminClients.selectedCustomerId;
    isInClientNs = adminClients.isInClientNamespace;
  } catch {
    // AdminClientsProvider not available
  }
  if (isAdmin && isInClientNs) return adminSelectedCustomerId;
  return payload?.userId ?? null;
}

